import { useState, useEffect } from "react";
import { CreditCard, Save, Loader2, DollarSign, Percent, Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  logAction: (action: string, entityType: string, entityId: string, details?: any) => void;
}

const AdminPaymentConfigTab = ({ logAction }: Props) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    gateway: "mercadopago",
    mp_platform_access_token: "",
    mp_platform_public_key: "",
    pix_enabled: true,
    credit_card_enabled: true,
    boleto_enabled: false,
    free_trial_days: 7,
    grace_period_days: 3,
    auto_suspend_on_overdue: true,
    send_payment_reminders: true,
    reminder_days_before: 3,
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    const { data } = await supabase.from("platform_settings")
      .select("*").eq("key", "payment_config").maybeSingle();
    if (data?.value) {
      setConfig(prev => ({ ...prev, ...(data.value as any) }));
    }
    setLoading(false);
  };

  const saveConfig = async () => {
    setSaving(true);
    const { data: existing } = await supabase.from("platform_settings")
      .select("id").eq("key", "payment_config").maybeSingle();

    if (existing) {
      await supabase.from("platform_settings").update({
        value: config as any,
        updated_by: (await supabase.auth.getUser()).data.user?.id,
      }).eq("id", existing.id);
    } else {
      await supabase.from("platform_settings").insert({
        key: "payment_config",
        value: config as any,
        updated_by: (await supabase.auth.getUser()).data.user?.id,
      });
    }

    logAction("payment_config_updated", "platform_settings", "payment_config", config);
    toast({ title: "Configurações de pagamento salvas!" });
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-semibold text-lg">Configuração de Pagamentos</h2>
        <p className="text-xs text-muted-foreground">Configure como os planos serão cobrados dos lojistas</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Gateway Config */}
        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="h-4 w-4 text-primary" />
            <h3 className="font-display font-semibold text-sm">Gateway de Pagamento</h3>
          </div>
          <div className="p-3 rounded-lg bg-secondary/50 text-xs text-muted-foreground">
            Credenciais da conta MercadoPago da plataforma para receber pagamentos de assinaturas dos lojistas.
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Access Token da Plataforma</Label>
            <Input
              type="password"
              value={config.mp_platform_access_token}
              onChange={e => setConfig(c => ({ ...c, mp_platform_access_token: e.target.value }))}
              placeholder="APP_USR-..."
              className="h-9 text-sm font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Public Key da Plataforma</Label>
            <Input
              value={config.mp_platform_public_key}
              onChange={e => setConfig(c => ({ ...c, mp_platform_public_key: e.target.value }))}
              placeholder="APP_USR-..."
              className="h-9 text-sm font-mono"
            />
          </div>
        </div>

        {/* Payment Methods */}
        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-4 w-4 text-primary" />
            <h3 className="font-display font-semibold text-sm">Métodos de Pagamento</h3>
          </div>
          <div className="space-y-3">
            {[
              { key: "pix_enabled" as const, label: "PIX", desc: "Pagamento instantâneo via PIX" },
              { key: "credit_card_enabled" as const, label: "Cartão de Crédito", desc: "Pagamento com cartão e parcelamento" },
              { key: "boleto_enabled" as const, label: "Boleto Bancário", desc: "Boleto com vencimento" },
            ].map(m => (
              <div key={m.key} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                <div>
                  <p className="text-sm font-medium">{m.label}</p>
                  <p className="text-[10px] text-muted-foreground">{m.desc}</p>
                </div>
                <Switch checked={config[m.key]} onCheckedChange={v => setConfig(c => ({ ...c, [m.key]: v }))} />
              </div>
            ))}
          </div>
        </div>

        {/* Trial & Grace */}
        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-4 w-4 text-primary" />
            <h3 className="font-display font-semibold text-sm">Trial & Carência</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Dias de Teste Grátis</Label>
              <Input type="number" min="0" value={config.free_trial_days}
                onChange={e => setConfig(c => ({ ...c, free_trial_days: parseInt(e.target.value) || 0 }))}
                className="h-9 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Dias de Carência</Label>
              <Input type="number" min="0" value={config.grace_period_days}
                onChange={e => setConfig(c => ({ ...c, grace_period_days: parseInt(e.target.value) || 0 }))}
                className="h-9 text-sm" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
              <div>
                <p className="text-sm font-medium">Suspender automaticamente</p>
                <p className="text-[10px] text-muted-foreground">Suspende loja após carência sem pagamento</p>
              </div>
              <Switch checked={config.auto_suspend_on_overdue}
                onCheckedChange={v => setConfig(c => ({ ...c, auto_suspend_on_overdue: v }))} />
            </div>
          </div>
        </div>

        {/* Reminders */}
        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Percent className="h-4 w-4 text-primary" />
            <h3 className="font-display font-semibold text-sm">Lembretes</h3>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
            <div>
              <p className="text-sm font-medium">Enviar lembretes de pagamento</p>
              <p className="text-[10px] text-muted-foreground">Notifica lojistas antes do vencimento</p>
            </div>
            <Switch checked={config.send_payment_reminders}
              onCheckedChange={v => setConfig(c => ({ ...c, send_payment_reminders: v }))} />
          </div>
          {config.send_payment_reminders && (
            <div className="space-y-1.5">
              <Label className="text-xs">Dias antes do vencimento</Label>
              <Input type="number" min="1" value={config.reminder_days_before}
                onChange={e => setConfig(c => ({ ...c, reminder_days_before: parseInt(e.target.value) || 3 }))}
                className="h-9 text-sm" />
            </div>
          )}
        </div>
      </div>

      <Button onClick={saveConfig} disabled={saving} className="bg-gradient-primary text-primary-foreground">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
        Salvar Configurações
      </Button>
    </div>
  );
};

export default AdminPaymentConfigTab;
